import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../services/apiServices";

// ✅ Fetch all service items
export const fetchServiceItems = createAsyncThunk(
  "serviceItems/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get("/service-items");
      return response.data.data; // Assuming response structure { message, data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch service items");
    }
  }
);

// ✅ Fetch a single service item by ID
export const fetchServiceItemById = createAsyncThunk(
  "serviceItems/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/service-items/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch service item");
    }
  }
);

// ✅ Create a new service item
export const createServiceItem = createAsyncThunk(
  "serviceItems/create",
  async (serviceItemData, { rejectWithValue }) => {
    try {
      const response = await apiService.post("/service-items", serviceItemData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create service item");
    }
  }
);

// ✅ Update a service item
export const updateServiceItem = createAsyncThunk(
  "serviceItems/update",
  async ({ id, serviceItemData }, { rejectWithValue }) => {
    try {
      const response = await apiService.put(`/service-items/${id}`, serviceItemData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update service item");
    }
  }
);

// ✅ Delete a service item
export const deleteServiceItem = createAsyncThunk(
  "serviceItems/delete",
  async (id, { rejectWithValue }) => {
    try {
      await apiService.delete(`/service-items/${id}`);
      return id; // Return the deleted ID to remove from the store
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete service item");
    }
  }
);

const serviceItemSlice = createSlice({
  name: "serviceItems",
  initialState: {
    items: [],
    selectedItem: null,
    status: "idle", // idle | loading | succeeded | failed
    error: null,
  },
  reducers: {
    clearSelectedServiceItem: (state) => {
      state.selectedItem = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all service items
      .addCase(fetchServiceItems.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchServiceItems.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload;
      })
      .addCase(fetchServiceItems.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Fetch single service item
      .addCase(fetchServiceItemById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchServiceItemById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedItem = action.payload;
      })
      .addCase(fetchServiceItemById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Create service item
      .addCase(createServiceItem.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createServiceItem.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items.push(action.payload);
      })
      .addCase(createServiceItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Update service item
      .addCase(updateServiceItem.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateServiceItem.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.items.findIndex((item) => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateServiceItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Delete service item
      .addCase(deleteServiceItem.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteServiceItem.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = state.items.filter((item) => item._id !== action.payload);
      })
      .addCase(deleteServiceItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearSelectedServiceItem } = serviceItemSlice.actions;
export default serviceItemSlice.reducer;
